import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Calendar, Plus, UserPlus, Edit2, Save, X } from 'lucide-react';
import { BoardMember } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { getStandupEntries, upsertStandupEntry } from '../firebase/firestore';

interface BoardMembersProps {
  members: BoardMember[];
}

const BoardMembers: React.FC<BoardMembersProps> = ({ members }) => {
  const [hoveredMember, setHoveredMember] = useState<BoardMember | null>(null);

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-500';
      case 'admin':
        return 'bg-red-500';
      case 'editor':
        return 'bg-blue-500';
      case 'viewer':
        return 'bg-gray-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="flex items-center space-x-2">
      <div className="flex items-center space-x-1">
        {members.slice(0, 5).map((member, index) => (
          <div
            key={member.userId}
            className="relative"
            onMouseEnter={() => setHoveredMember(member)}
            onMouseLeave={() => setHoveredMember(null)}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium cursor-pointer transition-transform hover:scale-110 ${getRoleColor(member.role)}`}
              style={{ zIndex: members.length - index }}
            >
              {getInitials(member.name || member.email)}
            </div>
            
            <AnimatePresence>
              {hoveredMember?.userId === member.userId && (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 5 }}
                  className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg whitespace-nowrap z-50"
                >
                  <div className="font-medium">{member.name || member.email}</div>
                  <div className="text-gray-300 capitalize">{member.role}</div>
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1 w-2 h-2 bg-gray-800 rotate-45"></div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
        
        {members.length > 5 && (
          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-xs font-medium">
            +{members.length - 5}
          </div>
        )}
      </div>
      
      <div className="flex items-center space-x-1 text-gray-500">
        <Users size={14} />
        <span className="text-xs">{members.length} member{members.length !== 1 ? 's' : ''}</span>
      </div>
    </div>
  );
};

// WhatAreWeDoing Component

interface StandupEntry {
  id?: string;
  userId: string;
  userName: string;
  userEmail: string;
  userPhotoURL?: string;
  focusedOn: string;
  nextUp: string;
  funFact: string;
  date: string;
  updatedAt: Date | string;
}

interface WhatAreWeDoingProps {
  boardId: string;
  boardMembers: any[];
}

const WhatAreWeDoing: React.FC<WhatAreWeDoingProps> = ({ 
  boardId, 
  boardMembers 
}) => {
  const { user } = useAuth();
  const [standupEntries, setStandupEntries] = useState<StandupEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ focusedOn: '', nextUp: '', funFact: '' });
  const [isSaving, setIsSaving] = useState(false);

  // Load standup entries from Firestore
  useEffect(() => {
    const loadStandupEntries = async () => {
      setIsLoading(true);
      try {
        // Get entries from Firestore
        const entries = await getStandupEntries(boardId, selectedDate);
        
        // Create entries for all board members, merging with Firestore data
        const allEntries: StandupEntry[] = boardMembers.map((member) => {
          const existingEntry = entries.find(e => e.userId === member.userId);
          if (existingEntry) {
            return {
              ...existingEntry,
              userName: member.name || member.email,
              userEmail: member.email,
            };
          }
          // Create empty entry for members without updates
          return {
            userId: member.userId,
            userName: member.name || member.email,
            userEmail: member.email,
            focusedOn: '',
            nextUp: '',
            funFact: '',
            date: selectedDate,
            updatedAt: new Date(),
          };
        });

        // Get profile photos for current user
        if (user) {
          const userEntry = allEntries.find(e => e.userId === user.id);
          if (userEntry) {
            userEntry.userPhotoURL = user.photoURL;
            userEntry.userName = user.name || userEntry.userName;
          }
        }

        setStandupEntries(allEntries);
      } catch (error) {
        console.error('Error loading standup entries:', error);
        // Fallback to empty entries
        const emptyEntries: StandupEntry[] = boardMembers.map((member) => ({
          userId: member.userId,
          userName: member.name || member.email,
          userEmail: member.email,
          focusedOn: '',
          nextUp: '',
          funFact: '',
          date: selectedDate,
          updatedAt: new Date(),
        }));
        setStandupEntries(emptyEntries);
      } finally {
        setIsLoading(false);
      }
    };

    if (boardId && boardMembers.length > 0) {
      loadStandupEntries();
    }
  }, [boardId, boardMembers, selectedDate, user]);

  const handleStartEdit = (entry: StandupEntry) => {
    if (entry.userId !== user?.id) return;
    setEditingEntryId(entry.id || entry.userId);
    setEditForm({
      focusedOn: entry.focusedOn || '',
      nextUp: entry.nextUp || '',
      funFact: entry.funFact || '',
    });
  };

  const handleCancelEdit = () => {
    setEditingEntryId(null);
    setEditForm({ focusedOn: '', nextUp: '', funFact: '' });
  };

  const handleSaveEntry = async () => {
    if (!user) return;
    
    setIsSaving(true);
    try {
      await upsertStandupEntry({
        boardId,
        userId: user.id,
        userName: user.name || user.email,
        userEmail: user.email,
        userPhotoURL: user.photoURL,
        focusedOn: editForm.focusedOn,
        nextUp: editForm.nextUp,
        funFact: editForm.funFact,
        date: selectedDate,
      });

      // Reload entries
      const entries = await getStandupEntries(boardId, selectedDate);
      const updatedEntries = standupEntries.map(entry => {
        if (entry.userId === user.id) {
          const updated = entries.find(e => e.userId === user.id);
          if (updated) {
            return {
              ...updated,
              userName: user.name || entry.userName,
              userEmail: user.email,
              userPhotoURL: user.photoURL,
            };
          }
          return {
            ...entry,
            focusedOn: editForm.focusedOn,
            nextUp: editForm.nextUp,
            funFact: editForm.funFact,
            updatedAt: new Date(),
          };
        }
        return entry;
      });
      setStandupEntries(updatedEntries);
      setEditingEntryId(null);
    } catch (error) {
      console.error('Error saving standup entry:', error);
      alert('Failed to save update. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInviteUsers = () => {
    // TODO: Open invite users modal
    console.log('Invite users to standup');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-satsuma-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-4 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1 sm:mb-2">What Are We Doing?</h1>
            <p className="text-sm sm:text-base text-gray-600">Daily standup updates for the team</p>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
            {/* Date Selector */}
            <div className="flex items-center space-x-2 glass-effect px-3 sm:px-4 py-2 rounded-xl border border-white/30">
              <Calendar size={16} className="sm:w-[18px] sm:h-[18px] text-satsuma-500 flex-shrink-0" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-transparent text-gray-800 focus:outline-none text-sm sm:text-base min-w-0"
              />
            </div>
            
            {/* Invite Users Button */}
            <button
              onClick={handleInviteUsers}
              className="flex items-center space-x-1.5 sm:space-x-2 px-3 sm:px-4 py-2 bg-satsuma-500 text-white rounded-xl hover:bg-satsuma-600 transition-colors touch-manipulation min-h-[44px]"
            >
              <UserPlus size={16} className="sm:w-[18px] sm:h-[18px]" />
              <span className="text-sm sm:text-base">Invite</span>
            </button>
          </div>
        </div>

        {/* Team Members Summary */}
        <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600">
          <div className="flex items-center space-x-2">
            <Users size={14} className="sm:w-4 sm:h-4" />
            <span>{boardMembers.length} team members</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span>{standupEntries.filter(e => e.focusedOn || e.nextUp || e.funFact).length} updates today</span>
          </div>
        </div>
      </div>

      {/* Standup Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {standupEntries.map((entry) => {
          const isCurrentUser = entry.userId === user?.id;
          const isEditing = editingEntryId === (entry.id || entry.userId);
          const getInitials = (name: string) => {
            return name
              .split(' ')
              .map(word => word.charAt(0))
              .join('')
              .toUpperCase()
              .slice(0, 2);
          };

          return (
            <div key={entry.id || entry.userId} className="glass-effect rounded-2xl p-4 sm:p-6 border border-white/30">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  {entry.userPhotoURL ? (
                    <img
                      src={entry.userPhotoURL}
                      alt={entry.userName}
                      className="w-10 h-10 rounded-full object-cover border-2 border-white"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gradient-to-br from-satsuma-400 to-satsuma-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      {getInitials(entry.userName)}
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-gray-800">{entry.userName}</h3>
                    <p className="text-sm text-gray-500">{entry.userEmail}</p>
                  </div>
                </div>
                {isCurrentUser && !isEditing && (
                  <button
                    onClick={() => handleStartEdit(entry)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
                    title="Edit your update"
                  >
                    <Edit2 size={16} className="text-gray-600" />
                  </button>
                )}
              </div>

            {/* Content */}
            <div className="space-y-4">
              {isEditing ? (
                // Edit Form
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <span>🎯</span>
                      <span>What I'm focused on today:</span>
                    </label>
                    <textarea
                      value={editForm.focusedOn}
                      onChange={(e) => setEditForm({ ...editForm, focusedOn: e.target.value })}
                      className="w-full px-3 py-2 bg-white/50 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-satsuma-500 text-gray-800 resize-none"
                      rows={3}
                      placeholder="What are you working on today?"
                    />
                  </div>

                  <div>
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <span>➡️</span>
                      <span>What comes next:</span>
                    </label>
                    <textarea
                      value={editForm.nextUp}
                      onChange={(e) => setEditForm({ ...editForm, nextUp: e.target.value })}
                      className="w-full px-3 py-2 bg-white/50 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-satsuma-500 text-gray-800 resize-none"
                      rows={3}
                      placeholder="What's coming up next?"
                    />
                  </div>

                  <div>
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <span>✨</span>
                      <span>Fun Fact:</span>
                    </label>
                    <textarea
                      value={editForm.funFact}
                      onChange={(e) => setEditForm({ ...editForm, funFact: e.target.value })}
                      className="w-full px-3 py-2 bg-white/50 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-satsuma-500 text-gray-800 resize-none"
                      rows={2}
                      placeholder="Share something fun!"
                    />
                  </div>

                  <div className="flex items-center justify-end space-x-2 pt-2">
                    <button
                      onClick={handleCancelEdit}
                      className="px-4 py-2 text-gray-700 hover:bg-white/20 rounded-lg transition-colors touch-manipulation min-h-[44px]"
                    >
                      <X size={16} className="inline mr-1" />
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveEntry}
                      disabled={isSaving}
                      className="flex items-center space-x-2 px-4 py-2 bg-satsuma-500 text-white rounded-lg hover:bg-satsuma-600 transition-colors disabled:opacity-50 touch-manipulation min-h-[44px]"
                    >
                      <Save size={16} />
                      <span>{isSaving ? 'Saving...' : 'Save'}</span>
                    </button>
                  </div>
                </div>
              ) : (
                // Display Mode
                <>
                  {/* Focused On */}
                  {entry.focusedOn && (
                    <div>
                      <div className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                        <span>🎯</span>
                        <span>What I'm focused on today:</span>
                      </div>
                      <p className="text-gray-800 bg-white/30 rounded-lg p-3">{entry.focusedOn}</p>
                    </div>
                  )}

                  {/* Next Up */}
                  {entry.nextUp && (
                    <div>
                      <div className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                        <span>➡️</span>
                        <span>What comes next:</span>
                      </div>
                      <p className="text-gray-800 bg-white/30 rounded-lg p-3">{entry.nextUp}</p>
                    </div>
                  )}

                  {/* Fun Fact */}
                  {entry.funFact && (
                    <div>
                      <div className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                        <span>✨</span>
                        <span>Fun Fact:</span>
                      </div>
                      <p className="text-gray-800 bg-white/30 rounded-lg p-3 italic">{entry.funFact}</p>
                    </div>
                  )}

                  {/* Empty State */}
                  {!entry.focusedOn && !entry.nextUp && !entry.funFact && (
                    <div className="text-center py-8 text-gray-500">
                      <div className="text-4xl mb-3">👤</div>
                      <p>No standup update yet</p>
                      {isCurrentUser && (
                        <button
                          onClick={() => handleStartEdit(entry)}
                          className="mt-3 px-4 py-2 bg-satsuma-500 text-white rounded-lg hover:bg-satsuma-600 transition-colors text-sm touch-manipulation min-h-[44px]"
                        >
                          Add Update
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            {!isEditing && (
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-xs text-gray-500">
                  Last updated: {new Date(entry.updatedAt).toLocaleString()}
                </p>
              </div>
            )}
          </div>
          );
        })}
      </div>

      {/* Empty State */}
      {standupEntries.length === 0 && (
        <div className="text-center py-16">
          <div className="glass-effect rounded-3xl p-8 max-w-md mx-auto">
            <Users size={64} className="mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No team members yet</h3>
            <p className="text-gray-600 mb-6">Invite team members to start sharing daily standup updates</p>
            <button
              onClick={handleInviteUsers}
              className="flex items-center space-x-2 mx-auto px-6 py-3 bg-satsuma-500 text-white rounded-xl hover:bg-satsuma-600 transition-colors"
            >
              <Plus size={20} />
              <span>Invite Team Members</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoardMembers;
export { WhatAreWeDoing };
